import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { FlaskConical, Users, Trash2, Loader2, Sparkles } from "lucide-react";
import { COUNTRIES } from "../../lib/countries";

export default function AdminTestZone() {
  const [quizzes, setQuizzes] = useState([]);
  const [quizId, setQuizId] = useState("");
  const [count, setCount] = useState(50);
  const [minSeconds, setMinSeconds] = useState(40);
  const [maxSeconds, setMaxSeconds] = useState(60);
  const [nationality, setNationality] = useState("random");
  const [testCount, setTestCount] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const loadQuizzes = async () => {
    const { data } = await api.get("/quizzes", { params: { type: "competition" } });
    setQuizzes(data || []);
    if ((data || []).length > 0 && !quizId) setQuizId(data[0].id);
  };
  const loadCount = async (qid) => {
    if (!qid) return;
    try {
      const { data } = await api.get("/admin/test-users/count", { params: { quiz_id: qid } });
      setTestCount(data.count || 0);
    } catch (_) { setTestCount(0); }
  };

  useEffect(() => { loadQuizzes(); }, []);
  useEffect(() => { loadCount(quizId); }, [quizId]);

  const generate = async () => {
    if (!quizId) { toast.error("Escolhe um quiz de prova"); return; }
    if (Number(minSeconds) > Number(maxSeconds)) { toast.error("O tempo mínimo não pode ser maior que o máximo"); return; }
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/test-users/generate", {
        quiz_id: quizId,
        count: Number(count),
        min_seconds: Number(minSeconds),
        max_seconds: Number(maxSeconds),
        nationality,
      });
      toast.success(`${data.created} utilizadores de teste gerados`);
      loadCount(quizId);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao gerar"); }
    setGenerating(false);
  };

  const deleteAll = async () => {
    if (!quizId) return;
    const quizName = quizzes.find((q) => q.id === quizId);
    if (!window.confirm(`Eliminar TODOS os utilizadores de teste do quiz "${quizName ? quizName.name_pt : quizId}"?\n\nIsto remove credenciais, participantes, resultados, ranking e diplomas de teste deste quiz. Ação irreversível.`)) return;
    setDeleting(true);
    try {
      const { data } = await api.delete("/admin/test-users", { params: { quiz_id: quizId } });
      toast.success(`Eliminados: ${data.deleted_credentials} utilizadores de teste`);
      loadCount(quizId);
    } catch (e) { toast.error("Erro ao eliminar"); }
    setDeleting(false);
  };

  return (
    <div data-testid="admin-test-zone-page" className="max-w-4xl">
      <div className="flex items-center gap-3 mb-2">
        <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-[#7C3AED] text-white"><FlaskConical size={22} /></span>
        <h1 className="font-outfit text-3xl font-bold text-[#0B1D3A]">Zona de Testes</h1>
      </div>
      <p className="text-sm text-[#0B1D3A]/55 mb-6">Gera utilizadores de teste com resultados aleatórios para testar rankings, diplomas e tabelas de resultados. Os nomes seguem o padrão TESTE1 / ESCOLA1…</p>

      <div className="rounded-2xl bg-white p-6 border border-[#0B1D3A]/5 space-y-4">
        <div>
          <label className="block text-sm font-semibold mb-1">Quiz de prova</label>
          <select value={quizId} onChange={(e) => setQuizId(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-[#0B1D3A]/15 bg-white" data-testid="testzone-quiz-select">
            {quizzes.length === 0 && <option value="">Sem quizzes de prova</option>}
            {quizzes.map((q) => <option key={q.id} value={q.id}>{q.name_pt}</option>)}
          </select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-semibold mb-1">Quantidade</label>
            <input type="number" min="1" max="500" value={count} onChange={(e) => setCount(parseInt(e.target.value) || 1)} className="w-full px-3 py-2 rounded-lg border border-[#0B1D3A]/15" data-testid="testzone-count" />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1">Tempo mínimo (s)</label>
            <input type="number" min="0" step="0.1" value={minSeconds} onChange={(e) => setMinSeconds(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-[#0B1D3A]/15" data-testid="testzone-min" />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1">Tempo máximo (s)</label>
            <input type="number" min="0" step="0.1" value={maxSeconds} onChange={(e) => setMaxSeconds(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-[#0B1D3A]/15" data-testid="testzone-max" />
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold mb-1">Nacionalidade</label>
          <select value={nationality} onChange={(e) => setNationality(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-[#0B1D3A]/15 bg-white" data-testid="testzone-nationality">
            <option value="random">Aleatória</option>
            {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.pt}</option>)}
          </select>
        </div>

        <button onClick={generate} disabled={generating || !quizId} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#7C3AED] text-white font-bold disabled:opacity-50" data-testid="testzone-generate-btn">
          {generating ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
          Gerar utilizadores de teste
        </button>
      </div>

      <div className="mt-6 rounded-2xl bg-white p-6 border border-[#0B1D3A]/5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#7C3AED]/10 text-[#7C3AED]"><Users size={20} /></span>
          <div>
            <p className="font-outfit font-bold text-[#0B1D3A]" data-testid="testzone-count-label">{testCount} utilizadores de teste</p>
            <p className="text-xs text-[#0B1D3A]/55">neste quiz de prova</p>
          </div>
        </div>
        <button onClick={deleteAll} disabled={deleting || testCount === 0} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 text-white font-semibold disabled:opacity-40" data-testid="testzone-delete-btn">
          {deleting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
          Eliminar utilizadores de teste
        </button>
      </div>
    </div>
  );
}
